'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Native `BeforeInstallPromptEvent` — not declared in lib.dom yet, so
 * we narrow the bit we use directly. Captured once per app load by
 * stashing the event in a module-level ref before any consumer
 * mounts; consumers then `prompt()` synchronously inside a user
 * gesture (Chromium requires the call to originate from a click
 * handler or the prompt is silently dropped).
 *
 * THE MODULE-LEVEL HALF IS EXPORTED (app-hosts plan E2, 2026-09-24): the
 * widget bridge answers an app's `install.state` / `install.request` from
 * inside a message handler, not a React render, so the captured prompt,
 * the platform guess and the standalone test are reachable as plain
 * functions (`getPwaInstallPrompt`, `promptPwaInstall`, `isStandaloneMode`,
 * `detectPlatform`, `subscribePwaInstall`). The hook is unchanged for its
 * existing callers.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let cachedPrompt: BeforeInstallPromptEvent | null = null;
let listenerAttached = false;
// `appinstalled` fired in THIS document. The installing tab keeps its browser
// display mode (the installed window opens beside it), so `display-mode`
// alone would still say "not installed" right after the event; the bridge
// and the header read this too, so the state flips and the `{ kind:
// 'install' }` broadcast follows the event as the app-hosts plan (E2) says.
let installedHere = false;
const subscribers = new Set<() => void>();

function notifySubscribers(): void {
  for (const fn of subscribers) fn();
}

function ensureListener(): void {
  if (typeof window === 'undefined') return;
  if (listenerAttached) return;
  listenerAttached = true;
  // Browser fires this BEFORE any React component mounts — if we
  // wait for the first hook caller, we'll have missed it. Attach
  // once at module load via `ensureListener()` invoked from the
  // module side-effect below.
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
    notifySubscribers();
  });
  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    installedHere = true;
    notifySubscribers();
  });
}

// Side-effect on module import — guaranteed to run before the first
// React component that imports the hook mounts, so we don't drop
// the early `beforeinstallprompt` event that Chromium dispatches
// before hydration finishes on some pages.
if (typeof window !== 'undefined') ensureListener();

export type PwaPlatform = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other';

export function detectPlatform(): PwaPlatform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent.toLowerCase();
  // iPadOS 13+ reports `MacIntel` userAgent but exposes touch points.
  if (/iphone|ipod/.test(ua)) return 'ios';
  if (
    /ipad/.test(ua) ||
    (ua.includes('mac') &&
      typeof navigator !== 'undefined' &&
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints &&
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1)
  )
    return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/mac/.test(ua)) return 'macos';
  if (/windows/.test(ua)) return 'windows';
  if (/linux/.test(ua)) return 'linux';
  return 'other';
}

export function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  // Chromium / desktop standalone detection.
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // iOS Safari sets `navigator.standalone` when launched from Home Screen.
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/** Did `appinstalled` fire in this document? See `installedHere`. */
export function wasInstalledHere(): boolean {
  ensureListener();
  return installedHere;
}

/** Has the browser handed us a deferred install prompt that is still unused? */
export function getPwaInstallPrompt(): boolean {
  ensureListener();
  return cachedPrompt !== null;
}

/**
 * Show the captured native prompt. Must run inside a user gesture. Resolves
 * `'unavailable'` when no prompt was captured (or it was already spent:
 * Chromium one-shots the event, and a second `beforeinstallprompt` only
 * follows a later visit).
 */
export async function promptPwaInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!cachedPrompt) return 'unavailable';
  try {
    await cachedPrompt.prompt();
    const result = await cachedPrompt.userChoice;
    cachedPrompt = null;
    notifySubscribers();
    return result.outcome;
  } catch {
    return 'dismissed';
  }
}

/** Be told when the prompt is captured, spent, or the app was installed. */
export function subscribePwaInstall(cb: () => void): () => void {
  ensureListener();
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

export interface UsePwaInstall {
  /**
   * `true` when the browser has surfaced a `beforeinstallprompt` event
   * (Chrome / Edge / Brave / Samsung Internet on Android, Chromium
   * variants on desktop). When `false`, callers should fall back to
   * platform-specific instructions.
   */
  canInstall: boolean;
  /** `true` when the app is already running as an installed PWA. */
  isInstalled: boolean;
  /** Best-effort host platform — drives the instruction copy. */
  platform: PwaPlatform;
  /**
   * Trigger the native install prompt. Returns the user's choice. Must
   * be invoked from inside a user-gesture handler (click / keydown);
   * otherwise the call resolves immediately to `dismissed`.
   */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

export function usePwaInstall(): UsePwaInstall {
  const [, force] = useState(0);

  useEffect(() => {
    const sub = () => force((n) => n + 1);
    return subscribePwaInstall(sub);
  }, []);

  const install = useCallback(() => promptPwaInstall(), []);

  return {
    canInstall: cachedPrompt !== null,
    isInstalled: isStandaloneMode() || installedHere,
    platform: detectPlatform(),
    install,
  };
}
